import { getApi } from 'common/api'
import cognito from 'common/cognito'
import { getStore } from 'common/store'
import { service } from 'common/service'
import { Req } from 'common/types/requests'
import { Res } from 'common/types/responses'
import { companyService } from './useCompany'

import { userDataActions } from './useUserData'

export const userService = service
  .enhanceEndpoints({ addTagTypes: ['User'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getUser: builder.query<Res['user'], Req['getUser']>({
        providesTags: ['User'],
        queryFn: async (args, api, _extraOptions, baseQuery) => {
          const cachedQuery = service.endpoints.getUser.select(args as any)(
            api.getState() as any,
          )
          const cachedUser: Res['user'] | null =
            (cachedQuery?.data as Res['user'] | undefined) || null

          let user: any = cachedUser
          if (getApi().getToken?.()) {
            return { data: cachedUser }
          }
          try {
            await cognito.getSession()
            user = await cognito.getUser()
          } catch (e) {
            return { data: cachedUser }
          }
          try {
            const meResponse = await baseQuery({
              method: 'GET',
              url: '/auth/customer/me',
            })
            if (meResponse?.data?.email) {
              user = {
                ...meResponse.data,
                username: meResponse.data.email,
              }
            }
          } catch (e) {}

          setTimeout(() => {
            getStore().dispatch(companyService.util.invalidateTags(['Company']))
          }, 0)
          return {
            data: user?.username ? user : cachedUser,
          }
        },
      }),
      login: builder.mutation<{}, Req['login']>({
        query: (query: Req['login']) => ({
          body: query,
          method: 'POST',
          url: `auth/customer/`,
        }),
      }),

      logout: builder.mutation<{}, Req['logout']>({
        queryFn: async (args, { dispatch, getState }) => {
          const state = getState()
          try {
            await cognito.logout()
          } catch (e) {}
          if (
            // User doesn't exist in the store state, skip resetting store
            !service.endpoints.getUser.select({})(state)?.data
          ) {
            return { data: {} }
          }
          await dispatch(service.util.resetApiState())
          await dispatch(userDataActions.setUserData(null))

          return { data: {} }
        },
      }),
      updateUser: builder.mutation<Res['user'], Req['updateUser']>({
        invalidatesTags: (res) => [
          { id: 'LIST', type: 'User' },
          { id: res?.id, type: 'User' },
        ],
        query: (query: Req['updateUser']) => ({
          body: query,
          method: 'PUT',
          url: `auth/customer/me/account-info`,
        }),
      }),

      verifyAccount: builder.mutation<
        Res['verifyAccount'],
        Req['verifyAccount']
      >({
        query: (query: Req['verifyAccount']) => {
          return {
            body: { token: query.token },
            method: 'POST',
            url: `auth/customer/verify`,
          }
        },
        transformResponse: async (res, _, req) => {
          try {
            await logout(getStore(), { skipClearDevSettings: true })
          } catch (e) {}
          await cognito.loginWithToken(req.username, res)
          getStore().dispatch(
            userDataActions.setUserData({
              first_name: res.first_name,
              last_name: res.last_name,
            }),
          )
          await getUser(getStore(), {}, { forceRefetch: true })
          return res
        },
      }),
      // END OF ENDPOINTS
    }),
  })
export const useUser = userService.endpoints.getUser.useQueryState
export const {
  useGetUserQuery,
  useLoginMutation,
  useLogoutMutation,
  useUpdateUserMutation,
  useVerifyAccountMutation,
  // END OF EXPORTS
} = userService

export async function logout(
  store: any,
  data: Req['logout'],
  options?: Parameters<typeof userService.endpoints.logout.initiate>[1],
) {
  await store.dispatch(userService.endpoints.logout.initiate(data, options))
}

export async function getUser(
  store: any,
  data: Req['getUser'],
  options?: Parameters<typeof userService.endpoints.getUser.initiate>[1],
) {
  const result = await store
    .dispatch(userService.endpoints.getUser.initiate(data, options))
    .unwrap()
  return result
}

// Call this on resolving login, if the login action returns a 401, treat it as an unconfirmed email
export const tryConfirmEmailRedirect = function (res: any) {
  if (res?.error?.status === 401) {
    cognito.getSession().then((session) => {
      const email = session.getIdToken().payload?.email
      getApi().confirmEmailRedirect(email)
    })
  }
}
